import React, { useEffect, useRef } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";

const CHECK_GREEN = "#10B981";
const ACTIVE_NAVY = "#1A4D5D";

interface AIPrescriptionFetchingProcessProps {
  /** Live WS `type: "status"` messages for this turn (oldest → newest). */
  statusMessages?: string[];
}

const AIPrescriptionFetchingProcess: React.FC<AIPrescriptionFetchingProcessProps> = ({
  statusMessages,
}) => {
  const steps = statusMessages && statusMessages.length > 0 ? statusMessages : [];
  const latestStepRef = useRef<HTMLDivElement | null>(null);

  // Follow each new status line into view (parent chat scroller picks this up via
  // scrollLatestBottomIntoView; this covers the case where the list itself is tall).
  useEffect(() => {
    if (steps.length === 0) return;
    const el = latestStepRef.current;
    if (!el) return;
    // Wait a frame so framer-motion's enter layout has applied.
    const id = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    });
    return () => cancelAnimationFrame(id);
  }, [steps.length]);

  if (steps.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
          py: 0.5,
        }}
      >
        <CircularProgress size={18} thickness={3.2} sx={{ color: ACTIVE_NAVY }} />
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-relevant="additions"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        width: "100%",
        // No maxHeight clip — show every status line; the conversation scroller
        // follows as lines append so the latest stays visible.
        py: 0.25,
      }}
    >
      {steps.map((label, index) => {
        const isCurrent = index === steps.length - 1;

        return (
          <Box
            key={`status-${index}-${label}`}
            ref={isCurrent ? latestStepRef : undefined}
          >
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    mt: "1px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isCurrent ? (
                    <CircularProgress
                      size={18}
                      thickness={3.2}
                      sx={{ color: ACTIVE_NAVY }}
                    />
                  ) : (
                    <CheckCircleIcon
                      sx={{ fontSize: 20, color: CHECK_GREEN }}
                    />
                  )}
                </Box>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    fontWeight: isCurrent ? 500 : 400,
                    fontSize: "0.95rem",
                    lineHeight: 1.4,
                    color: ACTIVE_NAVY,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            </motion.div>
          </Box>
        );
      })}
    </Box>
  );
};

export default AIPrescriptionFetchingProcess;
